import { type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import {
  GeneralAgentDetail,
  generalAgentDetailQuery,
  generalAgentSections,
} from '@/features/general-agents';
import { usePageMeta } from '@/app/shell/use-page-meta';
import { useRailSubNav } from '@/app/shell/use-rail-sub-nav';
import { resolveSection } from '@/lib/entity-sections';
import { generalAgentId } from '@/lib/api/ids';

export const Route = createFileRoute(
  '/_auth/general-agents/$generalAgentId/$section',
)({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      generalAgentDetailQuery(generalAgentId(params.generalAgentId)),
    ),
  component: GeneralAgentDetailPage,
});

/*
 * Injects a typed router <Link> into the domain-free RailSubNav. The rail sub-nav
 * builds the full `/general-agents/{id}/{section}` href; we pass it straight
 * through to `to` so every param survives — client navigation and deep-linking
 * both work.
 */
function renderLink(props: {
  href: string;
  children: ReactNode;
  className?: string;
  'aria-current'?: 'page';
  'data-section'?: string;
}) {
  const { href, children, ...rest } = props;
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}

function GeneralAgentDetailPage() {
  const { generalAgentId: generalAgentIdParam, section } = Route.useParams();
  const id = generalAgentId(generalAgentIdParam);
  const { data: generalAgent } = useSuspenseQuery(generalAgentDetailQuery(id));
  const activeSectionId =
    resolveSection(section, generalAgentSections) ??
    generalAgentSections[0]!.id;

  usePageMeta({ title: 'General Agent' });
  useRailSubNav({
    sections: generalAgentSections,
    activeSectionId,
    basePath: `/general-agents/${id}`,
    entityName: generalAgent.name,
    backHref: '/general-agents',
    backLabel: 'All General Agents',
    navLabel: 'General Agent sections',
    renderLink,
  });

  return (
    <Page>
      <PageHeader title="General Agent" />
      <Section>
        <GeneralAgentDetail generalAgentId={id} section={section} />
      </Section>
    </Page>
  );
}
